import { useEffect, useMemo, useState } from 'react'
import { Loader2, ArrowLeft, UserCircle2, ShieldCheck } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { useEphemeralState } from '../../lib/persist.js'
import {
  fmtPercent, relationLabel, scoreTone, TONE_CLASS, answerScore, isAnonymousRelation,
} from '../../lib/evaluation.js'

// One employee's round, opened up: every questionnaire written about them, with its score
// and free-text comment.
//
// TWO WAYS TO READ IT, because they answer different questions:
//
//   ตามผู้ประเมิน  one card per evaluator, their whole paper together — "what does อุ้ม think
//                  of this person". This is how the data is actually shaped.
//   ตามคำถาม       one block per question, every evaluator's answer side by side — "does
//                  anyone think they upsell well". A single 75% in one card is invisible;
//                  the same 75% from five people in a row is the finding.
//
// ตามคำถาม groups BY CATEGORY FIRST and only then by question, and that nesting is not
// cosmetic: each relation is answered on its OWN form (evaluation_rule picks a form per
// relation), so ผู้จัดการสาขา and ทันตแพทย์ are usually not answering the same questions at
// all. Pooling them under one question list would silently average two different questions
// that happen to share an id, or list the same label twice.
//
// The `evaluator` string arrives ALREADY redacted for peer rows unless the caller is
// HR/Admin — that decision is made in Xano (GET /evaluation/result/{id}/detail), not here.
// This component only renders what it is given, which is why there is no "show names"
// toggle: there is nothing to toggle, the name never reached the browser. ตามคำถาม numbers
// the anonymous rows (#1, #2…) so they can be told apart down a column; that reveals nothing
// ตามผู้ประเมิน did not already show, where each anonymous card IS one person's whole paper.
//
// Per-question scores are recomputed here from answers + the frozen form. Xano deliberately
// does not store them — it keeps the evaluator's raw answers untouched as the audit trail.

const MODES = [
  { key: 'evaluator', label: 'ตามผู้ประเมิน' },
  { key: 'question', label: 'ตามคำถาม' },
]

export default function EvaluationDetail({ resultId, onBack }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [mode, setMode] = useEphemeralState('emp:ev:detail:mode', 'evaluator')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    apiGet(`/api/hr/evaluation/result/${resultId}/detail`)
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setErr(e?.message || 'โหลดรายละเอียดไม่สำเร็จ') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [resultId])

  // Tasks bucketed by relation, ordered to match the คะแนนรวม bars above so the eye can move
  // between the two without re-reading. group_scores carries the canonical order; any relation
  // that somehow has tasks but no score row is appended rather than dropped.
  const sections = useMemo(() => {
    const tasks = data?.tasks || []
    const order = []
    const seen = new Set()
    for (const g of data?.group_scores || []) {
      if (g?.relation && !seen.has(g.relation)) { seen.add(g.relation); order.push(g.relation) }
    }
    for (const t of tasks) {
      if (t?.relation && !seen.has(t.relation)) { seen.add(t.relation); order.push(t.relation) }
    }
    return order
      .map((relation) => {
        const mine = tasks.filter((t) => t.relation === relation)
        const submitted = mine.filter((t) => t.status === 'submitted')
        return {
          relation,
          tasks: mine,
          submitted,
          labels: evaluatorLabels(submitted),
          blocks: questionBlocks(submitted),
          avg: (data?.group_scores || []).find((g) => g.relation === relation)?.avg_percent ?? null,
        }
      })
      .filter((s) => s.tasks.length)
  }, [data])

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={14} strokeWidth={1.75} /> กลับไปรายงาน
      </button>

      {err && <div className="text-[13px] text-rose-500 mb-3">{err}</div>}
      {loading && (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-400 py-10 justify-center">
          <Loader2 size={15} className="animate-spin" /> กำลังโหลด…
        </div>
      )}

      {!loading && data && (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 mb-4">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="text-[15px] font-semibold text-slate-900">คะแนนรวม</h3>
              <span className="text-[26px] font-semibold text-slate-900 tabular-nums leading-none">
                {fmtPercent(data.total_percent)}
              </span>
            </div>
            <div className="mt-3 space-y-2">
              {(data.group_scores || []).map((g) => (
                <div key={g.relation} className="flex items-center gap-3">
                  <div className="w-32 flex-none text-[12.5px] text-slate-500">{relationLabel(g.relation)}</div>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-500"
                      style={{ width: `${Math.max(0, Math.min(100, Number(g.avg_percent || 0)))}%` }}
                    />
                  </div>
                  <div className="w-16 text-right text-[12.5px] tabular-nums text-slate-700">{fmtPercent(g.avg_percent, 0)}</div>
                  <div className="w-24 text-right text-[11.5px] text-slate-400">
                    น้ำหนัก {g.weight_percent}% · {g.n_submitted}/{g.n_expected}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3">
            <div className="inline-flex rounded-xl ring-1 ring-slate-200 bg-white p-0.5">
              {MODES.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setMode(o.key)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition ${
                    mode === o.key ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {mode === 'question' && (
            <div className="space-y-3">
              {sections.map((s) => (
                <div key={s.relation} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                  <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5 flex-wrap">
                    <span className="text-[13.5px] font-medium text-slate-800">{relationLabel(s.relation)}</span>
                    <span className="text-[12px] text-slate-400">ตอบกลับ {s.submitted.length}/{s.tasks.length} คน</span>
                    {isAnonymousRelation(s.relation) && s.submitted.some((t) => t.anonymous) && (
                      <span className="text-[11.5px] text-slate-400">· ไม่เปิดเผยชื่อผู้ประเมิน</span>
                    )}
                    <span className={`ml-auto inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${
                      TONE_CLASS[scoreTone(s.avg)]
                    }`}>
                      {fmtPercent(s.avg, 0)}
                    </span>
                  </div>

                  {!s.submitted.length ? (
                    <div className="px-5 py-6 text-center text-[13px] text-slate-400">ยังไม่มีใครส่งแบบประเมินในกลุ่มนี้</div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {s.blocks.map(({ q, entries, avg }) => (
                        <div key={q.id} className="px-5 py-3">
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0 text-[13px] text-slate-700">{q.label}</div>
                            {q.type !== 'text' && (
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${
                                TONE_CLASS[scoreTone(avg)]
                              }`}>
                                {avg == null ? 'N/A' : `${Math.round(avg)}%`}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 space-y-1">
                            {entries.map(({ task, a, s: score }) => {
                              // A text question holds the words; skip the ones nobody wrote in
                              // rather than printing a column of em dashes.
                              if (q.type === 'text') {
                                if (!a?.value) return null
                                return (
                                  <div key={task.id} className="flex items-start gap-3">
                                    <div className="w-40 flex-none text-[12px] text-slate-400 truncate" title={s.labels.get(task.id)}>
                                      {s.labels.get(task.id)}
                                    </div>
                                    <div className="flex-1 text-[13px] text-slate-700 whitespace-pre-wrap">{a.value}</div>
                                  </div>
                                )
                              }
                              return (
                                <div key={task.id} className="flex items-center gap-3">
                                  <div className="w-40 flex-none text-[12px] text-slate-400 truncate" title={s.labels.get(task.id)}>
                                    {s.labels.get(task.id)}
                                  </div>
                                  <div className="flex-1 text-[12.5px] text-slate-600">{displayValue(q, a)}</div>
                                  <div className="w-12 text-right text-[12.5px] tabular-nums text-slate-400 flex-none">
                                    {score == null ? 'N/A' : `${Math.round(score * 100)}%`}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      ))}

                      {/* The overall free-text box, which belongs to the paper rather than to
                          any one question — kept last so it reads as the closing remark. */}
                      {s.submitted.some((t) => t.comment) && (
                        <div className="px-5 py-3 bg-slate-50/60">
                          <div className="text-[12px] text-slate-400 mb-1.5">ความเห็นเพิ่มเติม</div>
                          <div className="space-y-1.5">
                            {s.submitted.filter((t) => t.comment).map((t) => (
                              <div key={t.id} className="flex items-start gap-3">
                                <div className="w-40 flex-none text-[12px] text-slate-400 truncate" title={s.labels.get(t.id)}>
                                  {s.labels.get(t.id)}
                                </div>
                                <div className="flex-1 text-[13px] text-slate-700 whitespace-pre-wrap">{t.comment}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {mode === 'evaluator' && (
          <div className="space-y-3">
            {(data.tasks || []).map((t) => (
              <div key={t.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2.5 flex-wrap">
                  {t.anonymous
                    ? <ShieldCheck size={15} className="text-slate-400 flex-none" strokeWidth={1.75} />
                    : <UserCircle2 size={15} className="text-slate-400 flex-none" strokeWidth={1.75} />}
                  <span className="text-[13.5px] font-medium text-slate-800">{t.evaluator || '—'}</span>
                  <span className="text-[12px] text-slate-400">{relationLabel(t.relation)}</span>
                  {isAnonymousRelation(t.relation) && t.anonymous && (
                    <span className="text-[11.5px] text-slate-400">· ไม่เปิดเผยชื่อผู้ประเมิน</span>
                  )}
                  <span className={`ml-auto inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-medium ring-1 flex-none ${
                    t.status === 'submitted' ? TONE_CLASS[scoreTone(t.score_percent)] : TONE_CLASS.amber
                  }`}>
                    {t.status === 'submitted' ? fmtPercent(t.score_percent, 0) : 'ยังไม่ส่ง'}
                  </span>
                </div>

                {t.status === 'submitted' && (
                  <div className="divide-y divide-slate-50">
                    {(t.form?.questions || []).filter((q) => q.type !== 'text').map((q) => {
                      const a = (t.answers || []).find((x) => x.question_id === q.id)
                      const s = answerScore(q, a)
                      return (
                        <div key={q.id} className="px-5 py-2.5 flex items-center gap-3">
                          <div className="flex-1 min-w-0 text-[13px] text-slate-700">{q.label}</div>
                          <div className="text-[12.5px] text-slate-500 flex-none">{displayValue(q, a)}</div>
                          <div className="w-12 text-right text-[12.5px] tabular-nums text-slate-400 flex-none">
                            {s == null ? 'N/A' : `${Math.round(s * 100)}%`}
                          </div>
                        </div>
                      )
                    })}

                    {/* Free-text answers and the overall comment — the part HR actually reads. */}
                    {(t.form?.questions || []).filter((q) => q.type === 'text').map((q) => {
                      const a = (t.answers || []).find((x) => x.question_id === q.id)
                      if (!a?.value) return null
                      return (
                        <div key={q.id} className="px-5 py-3">
                          <div className="text-[12px] text-slate-400 mb-1">{q.label}</div>
                          <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{a.value}</div>
                        </div>
                      )
                    })}
                    {t.comment && (
                      <div className="px-5 py-3 bg-slate-50/60">
                        <div className="text-[12px] text-slate-400 mb-1">ความเห็นเพิ่มเติม</div>
                        <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{t.comment}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </>
      )}
    </div>
  )
}

// A stable name per evaluator within one category. Anonymous peers all arrive carrying the
// SAME redacted string, which is fine when each one owns a card and useless in a column, so
// they are numbered in task order — stable across renders because the list they come from is.
function evaluatorLabels(tasks) {
  const m = new Map()
  let anon = 0
  for (const t of tasks) {
    m.set(t.id, t.anonymous ? `ไม่ระบุชื่อ #${++anon}` : (t.evaluator || '—'))
  }
  return m
}

// One block per question, with every evaluator's answer under it and the group's mean.
//
// Questions are collected as a UNION keyed by question id rather than taken from the first
// paper: a form edited mid-round leaves earlier tasks holding a form_snapshot that no longer
// matches the later ones, and the snapshot is deliberate — reading only the first would hide
// a question the rest were asked. First definition seen wins for the label and scale, which
// is the one the summary bars were computed against.
//
// The mean is over evaluators who actually scored the question: N/A and unanswered drop out
// of both sides of the division, exactly as taskScorePercent and Xano's scorer do.
function questionBlocks(tasks) {
  const qmap = new Map()
  for (const t of tasks) {
    for (const q of t.form?.questions || []) {
      if (q?.id && !qmap.has(q.id)) qmap.set(q.id, q)
    }
  }
  return [...qmap.values()].map((q) => {
    const entries = tasks.map((task) => {
      const a = (task.answers || []).find((x) => x.question_id === q.id)
      return { task, a, s: answerScore(q, a) }
    })
    const scored = entries.filter((e) => e.s != null)
    const avg = scored.length ? (scored.reduce((sum, e) => sum + e.s, 0) / scored.length) * 100 : null
    return { q, entries, avg }
  })
}

function displayValue(q, a) {
  if (!a) return '—'
  if (a.na) return 'ไม่มีข้อมูล'
  if (q.type === 'dropdown') {
    return (q.options || []).find((o) => o.value === a.value)?.label || a.value || '—'
  }
  if (q.type === 'yesno') return String(a.value).toLowerCase() === 'yes' ? 'ใช่' : 'ไม่ใช่'
  if (q.type === 'scale') {
    const hi = Number(q.scale?.max ?? 5)
    return `${a.value ?? '—'} / ${hi}`
  }
  return String(a.value ?? '—')
}
